import {useEffect, useCallback} from 'react';
import {useId} from './useId';

type EventDetail<T> = {origin: string; value: T};

type DispatchFn<T> = (value: T) => void;
type HandlerFn<T> = (value: T) => void;

export function useCustomEvent<T = undefined>(name: string, handler: HandlerFn<T>): DispatchFn<T> {
	const id = useId();

	useEffect(() => {
		const eventDetailHandler = (event: CustomEvent<EventDetail<T>>) => {
			if (event.detail.origin !== id) handler(event.detail.value);
		};
		window.addEventListener(name, eventDetailHandler);
		return () => window.removeEventListener(name, eventDetailHandler);
	}, [id, name, handler]);

	const dispatchEvent = useCallback(
		(value: T) => {
			const event = new CustomEvent<EventDetail<T>>(name, {detail: {origin: id, value}});
			window.dispatchEvent(event);
		},
		[name]
	);

	return dispatchEvent;
}
